import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { Alert, Box, CircularProgress, Link, Typography, useTheme } from '@mui/material';

type Props = {
    markdownUrl: string;
    title?: string;
};

function isExternalLink(href: string): boolean {
    return /^https?:\/\//i.test(href);
}

export default function MarkdownDocPage({ markdownUrl, title }: Props) {
    const theme = useTheme();
    const [markdown, setMarkdown] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setMarkdown(null);
        setError(null);

        const run = async () => {
            try {
                const res = await fetch(markdownUrl);
                if (!res.ok) throw new Error(`Failed to load markdown (HTTP ${res.status})`);
                const text = await res.text();
                if (!cancelled) setMarkdown(text);
            } catch {
                if (!cancelled) setError('Failed to load this documentation page.');
            }
        };

        void run();
        return () => {
            cancelled = true;
        };
    }, [markdownUrl]);

    const sx = useMemo(
        () => ({
            '& h1': { ...theme.typography.h4, fontWeight: 700, marginTop: 0, marginBottom: theme.spacing(1) },
            '& h2': {
                ...theme.typography.h5,
                fontWeight: 700,
                marginTop: theme.spacing(3),
                marginBottom: theme.spacing(1),
                scrollMarginTop: theme.spacing(10)
            },
            '& h3': {
                ...theme.typography.h6,
                fontWeight: 700,
                marginTop: theme.spacing(2.5),
                marginBottom: theme.spacing(1),
                scrollMarginTop: theme.spacing(10)
            },
            '& p': { ...theme.typography.body1, color: theme.palette.text.primary, margin: `${theme.spacing(1)} 0` },
            '& ul, & ol': { paddingLeft: theme.spacing(3), margin: `${theme.spacing(1)} 0` },
            '& li': { marginBottom: theme.spacing(0.75) },
            '& code': {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: '0.875em',
                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                padding: '0.1em 0.35em',
                borderRadius: 6
            },
            '& pre code': {
                display: 'block',
                padding: theme.spacing(1.5),
                overflowX: 'auto'
            },
            '& blockquote': {
                margin: `${theme.spacing(2)} 0`,
                paddingLeft: theme.spacing(2),
                borderLeft: `4px solid ${theme.palette.divider}`,
                color: theme.palette.text.secondary
            },
            '& img': {
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: 12,
                border: `1px solid ${theme.palette.divider}`,
                margin: `${theme.spacing(1.5)} 0`
            },
            '& hr': { border: 'none', borderTop: `1px solid ${theme.palette.divider}`, margin: `${theme.spacing(3)} 0` },
            '& table': { width: '100%', borderCollapse: 'collapse', margin: `${theme.spacing(2)} 0` },
            '& th, & td': { borderBottom: `1px solid ${theme.palette.divider}`, padding: theme.spacing(1), textAlign: 'left' }
        }),
        [theme]
    );

    if (error) return <Alert severity="error">{error}</Alert>;
    if (!markdown) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={160}>
                <CircularProgress size={22} />
            </Box>
        );
    }

    return (
        <Box sx={sx}>
            {title ? (
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 1 }}>
                    {title}
                </Typography>
            ) : null}
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSlug]}
                components={{
                    a: ({ href, children }) => {
                        const url = String(href || '');
                        if (!url) return <>{children}</>;
                        const external = isExternalLink(url);
                        return (
                            <Link
                                href={url}
                                target={external ? '_blank' : undefined}
                                rel={external ? 'noreferrer' : undefined}
                            >
                                {children}
                            </Link>
                        );
                    }
                }}
            >
                {markdown}
            </ReactMarkdown>
        </Box>
    );
}

